// src/components/WeatherFinder.jsx
import { useEffect, useMemo, useState } from "react";
import { trackEvent } from "../lib/analytics";
import { getFeatureLimit } from "../config/features";
import { rankCalmest, rankDriest, rankWarmest } from "../lib/weatherFinderRanking";
import { getTmaxWindowCompleteness } from "../lib/weatherFinderDisplay";
import { distanceKm } from "../utils/distance";
import WeatherFinderCard from "./WeatherFinderCard";

const INITIAL_VISIBLE_COUNT = 10;
const MODES = ["calmest", "warmest", "driest"];
const RADIUS_OPTIONS = [50, 100, 200, null];
const DAYS_OPTIONS = [3, 5, 7];

const MODE_KEY = {
  calmest: "weatherFinderCalmest",
  warmest: "weatherFinderWarmest",
  driest: "weatherFinderDriest",
};

const MODE_ICON = { calmest: "🌫️", warmest: "🔥", driest: "💧" };

const RESULT_TITLE_KEY = {
  calmest: "weatherFinderResultTitleCalmest",
  warmest: "weatherFinderResultTitleWarmest",
  driest: "weatherFinderResultTitleDriest",
};

export default function WeatherFinder({ siteList, scoresById, userLoc, entitlements, units, t, onUpgrade, onSelectSite }) {
  const isPro = !!entitlements?.isPro;

  const [mode, setMode] = useState("calmest");
  const [radiusKm, setRadiusKm] = useState(100);
  const [days, setDays] = useState(3);
  const [showAll, setShowAll] = useState(false);

  // UX Miði #376 follow-up — supporting-detail disclosure. Presentation-only:
  // every hook below runs unconditionally, same as before this change. sites/
  // ranked are a pure downstream computation of the shared scoresById prop
  // (App.jsx's useLeaderboardScores) — they feed nothing back into
  // comparisonState, so this component's UI state cannot affect it either way.
  const [finderOpen, setFinderOpen] = useState(false);

  useEffect(() => {
    setShowAll(false);
  }, [mode, radiusKm, days]);

  const resultsLimit = getFeatureLimit("weatherFinderResultsCount", entitlements) ?? 3;

  const sites = useMemo(() => {
    if (!siteList?.length || !scoresById) return [];
    return siteList
      .filter((site) => scoresById[site.id]?.rows?.length)
      .map((site) => ({
        id: site.id,
        name: site.name,
        distanceKm: userLoc ? distanceKm(userLoc, site) : null,
        forecast: scoresById[site.id].rows,
      }));
  }, [siteList, scoresById, userLoc]);

  const effectiveDays = isPro ? days : 3;
  const effectiveRadius = isPro ? radiusKm : 100;

  const options = useMemo(() => ({
    days: effectiveDays,
    maxDistanceKm: userLoc ? effectiveRadius : null,
  }), [effectiveDays, effectiveRadius, userLoc]);

  const ranked = useMemo(() => {
    const rankFn = mode === "calmest" ? rankCalmest : mode === "warmest" ? rankWarmest : rankDriest;
    return rankFn(sites, options).slice(0, resultsLimit);
  }, [mode, sites, options, resultsLimit]);

  // Ticket #444 (J5) — built from the SAME `sites`/`options.days` ranking
  // itself uses, keyed by stable site id (never by position in `ranked`,
  // which is sorted/sliced and so cannot be used to recover each site's own
  // forecast rows). Presentation-only: never touches `ranked`'s own order,
  // score, or metrics.
  const warmestCompletenessById = useMemo(() => {
    const map = {};
    for (const site of sites) {
      map[site.id] = getTmaxWindowCompleteness(site.forecast, options.days);
    }
    return map;
  }, [sites, options.days]);

  const visibleResults = showAll ? ranked : ranked.slice(0, INITIAL_VISIBLE_COUNT);
  const hasMore = ranked.length > INITIAL_VISIBLE_COUNT;

  // Ticket 415 (#415): the owner-clarified selection behavior — activating a
  // visible result must match selecting a place in the top list exactly:
  // same site-id-based callback (App's handleSelectSite, unchanged/reused,
  // never duplicated here), same map-scroll side effect. Fired once per
  // genuine activation, before onSelectSite, honestly describing this as a
  // `weather_finder` list click (no canonical stay/move verdict exists
  // here) rather than reusing weekly_ranking_site_clicked, which belongs to
  // a different surface.
  function handleSelectResult(result) {
    if (!result?.id) return;
    trackEvent("recommendation_destination_clicked", {
      destination_id: result.id,
      destination_name: result.name,
      recommendation_type: "weather_finder",
      reason: mode,
    });
    onSelectSite(result.id);
  }

  if (!sites.length) return null;

  if (!finderOpen) {
    return (
      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 shadow-sm">
        <div className="mb-4 text-base font-bold">{t("weatherFinderTitle")}</div>

        <button
          type="button"
          onClick={() => setFinderOpen(true)}
          aria-expanded={false}
          aria-controls="weather-finder-panel"
          className="w-full flex items-center justify-between gap-2 rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2.5 text-sm font-semibold text-sky-700 dark:text-sky-300 hover:bg-sky-50/60 dark:hover:bg-slate-800/60"
        >
          <span className="inline-flex items-center gap-2">
            <span aria-hidden>🔍</span>
            {t("weatherFinderShowDetailsCta")}
          </span>
          <span aria-hidden>→</span>
        </button>
      </div>
    );
  }

  const activeBtn = "bg-slate-900 text-white dark:bg-white dark:text-slate-900";
  const inactiveBtn = "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700";

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="text-base font-bold">{t("weatherFinderTitle")}</div>
        <button
          type="button"
          onClick={() => setFinderOpen(false)}
          aria-expanded={true}
          aria-controls="weather-finder-panel"
          className="shrink-0 text-xs font-semibold underline decoration-dotted underline-offset-2 opacity-75 hover:opacity-100"
        >
          {t("weatherFinderHideDetailsCta")}
        </button>
      </div>

      <div id="weather-finder-panel">
      {/* Dynamic title */}
      <div className="mb-4 text-base font-bold">
        {t(RESULT_TITLE_KEY[mode])}
        <span className="ml-2 text-sm font-normal text-slate-400 dark:text-slate-500">
          · {effectiveDays} {t("days").toLowerCase()}
        </span>
      </div>

      {/* Mode tabs */}
      <div className="mb-4 flex gap-2">
        {MODES.map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => {
              if (m !== mode) {
                trackEvent("weather_finder_mode_changed", { mode: m, previousMode: mode, isPro });
              }
              setMode(m);
            }}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${mode === m ? activeBtn : inactiveBtn}`}
          >
            <span aria-hidden="true">{MODE_ICON[m]}</span>{" "}{t(MODE_KEY[m])}
          </button>
        ))}
      </div>

      {/* Pro controls */}
      {isPro && (
        <div className="mb-4 flex flex-wrap gap-x-6 gap-y-3">
          <div>
            <div className="mb-1 text-xs text-slate-500 dark:text-slate-400">{t("weatherFinderRadius")}</div>
            <div className="flex gap-1.5">
              {RADIUS_OPTIONS.map((r) => (
                <button
                  key={r ?? "unlimited"}
                  type="button"
                  onClick={() => setRadiusKm(r)}
                  className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${radiusKm === r ? activeBtn : inactiveBtn}`}
                >
                  {r != null ? `${r} km` : t("weatherFinderUnlimited")}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs text-slate-500 dark:text-slate-400">{t("weatherFinderDays")}</div>
            <div className="flex gap-1.5">
              {DAYS_OPTIONS.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDays(d)}
                  className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${days === d ? activeBtn : inactiveBtn}`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {ranked.length === 0 ? (
        <div className="py-4 text-center">
          <div className="text-sm text-slate-500 dark:text-slate-400">{t("weatherFinderNoResults")}</div>
          <div className="mt-1 text-xs text-slate-400 dark:text-slate-500">{t("weatherFinderNoResultsHint")}</div>
        </div>
      ) : (
        <>
          <div className="space-y-1.5">
            {visibleResults.map((result, i) => (
              <WeatherFinderCard
                key={result.id}
                result={result}
                rank={i + 1}
                mode={mode}
                units={units}
                t={t}
                onSelect={typeof onSelectSite === "function" ? handleSelectResult : undefined}
                completeness={mode === "warmest" ? warmestCompletenessById[result.id] : undefined}
              />
            ))}
          </div>

          {hasMore && (
            <button
              type="button"
              onClick={() => {
                if (!showAll) {
                  trackEvent("weather_finder_expanded", { mode, isPro, resultsLimit });
                }
                setShowAll((v) => !v);
              }}
              className={`mt-3 w-full rounded-xl px-4 py-2 text-sm font-medium transition-colors ${inactiveBtn}`}
            >
              {showAll ? t("weatherFinderShowTop10") : t("weatherFinderShowFull")}
            </button>
          )}
        </>
      )}

      {/* Free upsell */}
      {!isPro && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => {
              trackEvent("weather_finder_upgrade_clicked", {
                source: "weather_finder",
                userTier: "free",
                mode,
              });
              if (typeof onUpgrade === "function") onUpgrade("weather_finder");
            }}
            className="w-full rounded-xl px-3 py-2 text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-400/60 dark:bg-emerald-500 dark:hover:bg-emerald-400"
          >
            {t("weatherFinderUpgradeForMore")}
          </button>
        </div>
      )}
      </div>
    </div>
  );
}
